<template>
  <div class="w-100">
    <CollectionContentHeader :item="collection" :name="$t('main.groupers.collections.title')" />
    <b-container fluid>
      <b-row>
        <b-col>
          <div class="w-100">
            <div v-if="isLoading" class="position-absolute w-100">
              <Loading />
            </div>
            <GridCSS v-else :items="collectionContent" :send-vector-click="true">
              <template #default="slotProps">
                <NormalVectorOverlay :data="slotProps.data" />
              </template>
            </GridCSS>
          </div>
          <GrouperPaginator
            name-page="collection"
            :total-pages="collectionTotalPages"
            :current-page="collectionCurrentPage"
          />

          <Sector class="mb-5">
            <Subtitle :margin-right="5">
              <template #text>{{ $t('main.groupers.collections.carouselTitle') }}</template>
            </Subtitle>
            <Carousel
              :items="latestCollections"
              :height="230"
              :margin-bottom="45"
              :see-more-enabled="true"
              :see-more-link="$trailingSlash(localePath('collections'))"
              :show-bottom-bar="true"
              :is-grouper="true"
              :is-collection="true"
              @item-click="gaEvent($event, 'Click Collections Carrusel Preview Collection')"
            >
              <template #bottomBar="slotProps">
                <CollectionsBottomBar :data="slotProps.data" />
              </template>
            </Carousel>
          </Sector>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import Carousel from '@/components/Common/Carousel';
import CollectionsBottomBar from '@/components/Collections/CollectionsBottomBar';
import GridCSS from '@/components/Common/GridCSS';
import GrouperPaginator from '@/components/Groupers/GrouperPaginator';
import CollectionContentHeader from '@/components/Collections/CollectionContentHeader';
import Loading from 'UI/Loading';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import Sector from '@/components/Home/Sector';
import Subtitle from '@/components/Home/Subtitle';

export default {
  nuxtI18n: {
    paths: {
      en: '/collections/:slug',
      de: '/sammlungen/:slug',
      es: '/colecciones/:slug',
      pt: '/colecoes/:slug'
    }
  },
  components: {
    Carousel,
    CollectionsBottomBar,
    GridCSS,
    GrouperPaginator,
    CollectionContentHeader,
    Loading,
    NormalVectorOverlay,
    Sector,
    Subtitle
  },
  async validate({ params, store }) {
    const currentPage = params.page ? parseInt(params.page) : 1;

    store.dispatch('collections/CLEAN_COLLECTIONS');
    await Promise.all([
      store.dispatch('collections/LOAD_COLLECTION', { slug: params.slug }),
      store.dispatch('collections/LOAD_COLLECTION_CONTENT', {
        slug: params.slug,
        page: currentPage
      })
    ]);
    return store.state.collections.collectionContent.length;
  },
  async fetch({ store, params }) {
    const { en, de, es, pt } = store.state.collections.collection.metadata;
    const slugs = {
      en: { slug: en },
      de: { slug: de },
      es: { slug: es },
      pt: { slug: pt }
    };
    store.dispatch('i18n/setRouteParams', slugs);

    await store.dispatch('collections/LOAD_LATEST_COLLECTIONS', { exclude: params.slug });
  },
  head() {
    const metadata = this.$store.state.collections.collection.metadata;
    const i18nSeo = this.$seoPlugin({ slash: true, canonicalLink: metadata?.cannonical });

    const openGraph = this.$openGraphPlugin(
      metadata?.metaTitle,
      'website',
      this.$route.path,
      metadata?.metaDescription,
      this.getImageFirstResult,
      this.$store.state.i18n.locale
    );

    return {
      title: metadata?.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: metadata?.metaDescription
        },
        ...openGraph
      ],
      link: [...i18nSeo.link]
    };
  },
  computed: {
    ...mapState('collections', [
      'collection',
      'collectionContent',
      'collectionTotalPages',
      'collectionCurrentPage',
      'isLoading',
      'latestCollections'
    ]),
    ...mapGetters('collections', ['getImageFirstResult'])
  }
};
</script>

<style lang="scss" scoped>
h1 {
  font-size: 27px;
}
p {
  font-size: 16px;
}
.badge-secondary {
  background: #b8aea4;
}
</style>
